// Tests for this utility are covered by the Axios interceptor integration tests:
// src/restAPI/__tests__/index.interceptor.test.ts
import { APICallContextEnum } from 'types/api/APICallContextEnum';

export type APICallContext = {
  acSource: APICallContextEnum;
  acDashboardId?: string;
};

/**
 * Extracts the feature pathname from a hash-router URL by stripping:
 *   1. The leading '#'
 *   2. The tenant/BU prefix: /u/{tenant}/{businessUnit}
 *   3. Any query string or fragment
 *
 * Returns the feature-level path starting from the first segment after the BU,
 * e.g. '/dashboard/42', '/findings', '/dashboard/edit/5'.
 *
 * Returns '' if the hash doesn't match the expected /u/{tenant}/{bu}/... structure.
 *
 * Examples:
 *   '#/u/612/2689/dashboard/42?foo=bar'  → '/dashboard/42'
 *   '#/u/612/2689/findings'              → '/findings'
 *   '#/u/612/2689/findings/123'          → '/findings/123'
 */
function getHashPathname(hash: string): string {
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  // Strip query string and fragment
  const path = withoutHash.split('?')[0].split('#')[0];
  // Strip /u/{tenant}/{businessUnit} prefix — exactly 3 leading segments
  // Pattern: /u/<segment>/<segment><remainder>
  const match = path.match(/^\/u\/[^/]+\/[^/]+(\/.*)?$/);
  if (!match) return '';
  return match[1] ?? '';
}

/**
 * Derives the API call context from the current hash URL.
 * Used by the Axios request interceptor to inject x-ac-source / x-ac-dashboard-id headers.
 *
 * Matching priority (first match wins):
 *   1.  /dashboard/create              → DASHBOARD_EDIT
 *   2.  /dashboard/edit/{id}           → DASHBOARD_EDIT
 *   3.  /dashboard/product/            → PRODUCT_DETAILS   (ENG-137551)
 *   4.  /dashboard/{id}                → DASHBOARD          (id must be > 0; no further segments)
 *   5.  /findings/{id}  (exact, int)   → FINDING_DETAILS    (ENG-137551)
 *   6.  /findings (any sub-path)       → FINDING_LIST
 *   7.  /product                       → PRODUCT_LIST       (ENG-137551)
 *   8.  /subProduct                    → SUBPRODUCT_LIST    (ENG-137551)
 *   9.  /ticketListing                 → TICKET_LIST        (ENG-137551)
 *   10. /onboarding                    → ONBOARDING         (ENG-137551)
 *   11. /groups/chart                  → HIERARCHY_DETAILS  (ENG-137551)
 *   12. /groups                        → HIERARCHIES        (ENG-137551)
 *   13. /project                       → ENGAGEMENT_DETAILS (ENG-137551)
 *   14. /assets                        → ASSET_DETAILS      (ENG-137551)
 *   15. anything else                  → BLANK
 *
 * Rejects:
 *   - Dashboard ID 0 (maps to BLANK)
 *   - Non-numeric dashboard IDs (maps to BLANK)
 *   - Dashboard/findings patterns appearing only in query params or subpaths
 */
export function getAPICallContext(hash: string): APICallContext {
  const pathname = getHashPathname(hash);

  // ── Dashboard family ──────────────────────────────────────────────────────

  if (pathname.startsWith('/dashboard/create')) {
    return { acSource: APICallContextEnum.DASHBOARD_EDIT };
  }

  if (pathname.startsWith('/dashboard/edit/')) {
    return { acSource: APICallContextEnum.DASHBOARD_EDIT };
  }

  // Product dashboard — must come before the generic /dashboard/{id} check
  if (pathname.startsWith('/dashboard/product/')) {
    return { acSource: APICallContextEnum.PRODUCT_DETAILS };
  }

  if (pathname.startsWith('/dashboard/')) {
    const afterDashboard = pathname
      .slice('/dashboard/'.length)
      .replace(/\/$/, '');
    // View mode: must be a single positive integer — no further path segments
    if (!afterDashboard.includes('/')) {
      const idNum = Number(afterDashboard);
      if (Number.isInteger(idNum) && idNum > 0) {
        return {
          acSource: APICallContextEnum.DASHBOARD,
          acDashboardId: afterDashboard,
        };
      }
    }
  }

  // ── Findings family ───────────────────────────────────────────────────────

  // Finding detail — exact two-segment path /findings/{numericId}
  // Must come before the FINDING_LIST catch-all so /findings/123 → FINDING_DETAILS
  // while /findings/createTicket and /findings/123/attack-path → FINDING_LIST.
  //
  // Strict positive-decimal guard: only canonical positive decimal integers are
  // accepted — a non-zero leading digit followed by zero or more digits.
  // Examples: "123" ✓  "001" ✗  "1e3" ✗  "0x10" ✗  "+1" ✗  "1.0" ✗  "0" ✗
  if (pathname.startsWith('/findings/')) {
    const afterFindings = pathname
      .slice('/findings/'.length)
      .replace(/\/$/, '');
    if (/^[1-9]\d*$/.test(afterFindings)) {
      return { acSource: APICallContextEnum.FINDING_DETAILS };
    }
  }

  if (pathname === '/findings' || pathname.startsWith('/findings/')) {
    return { acSource: APICallContextEnum.FINDING_LIST };
  }

  // ── Inventory / listing pages ─────────────────────────────────────────────

  if (pathname === '/product' || pathname.startsWith('/product/')) {
    return { acSource: APICallContextEnum.PRODUCT_LIST };
  }

  if (pathname === '/subProduct' || pathname.startsWith('/subProduct/')) {
    return { acSource: APICallContextEnum.SUBPRODUCT_LIST };
  }

  if (pathname === '/ticketListing' || pathname.startsWith('/ticketListing/')) {
    return { acSource: APICallContextEnum.TICKET_LIST };
  }

  if (pathname === '/onboarding' || pathname.startsWith('/onboarding/')) {
    return { acSource: APICallContextEnum.ONBOARDING };
  }

  // ── Hierarchy / groups ────────────────────────────────────────────────────

  // Chart detail must come before the groups listing check
  if (pathname === '/groups/chart' || pathname.startsWith('/groups/chart/')) {
    return { acSource: APICallContextEnum.HIERARCHY_DETAILS };
  }

  if (pathname === '/groups' || pathname.startsWith('/groups/')) {
    return { acSource: APICallContextEnum.HIERARCHIES };
  }

  // ── Engagements & assets ──────────────────────────────────────────────────

  if (pathname === '/project' || pathname.startsWith('/project/')) {
    return { acSource: APICallContextEnum.ENGAGEMENT_DETAILS };
  }

  if (pathname === '/assets' || pathname.startsWith('/assets/')) {
    return { acSource: APICallContextEnum.ASSET_DETAILS };
  }

  return { acSource: APICallContextEnum.BLANK };
}
